/**
 * Activities API Client
 *
 * Handles fetching and managing unified activities from the backend.
 * Includes support for:
 * - Paginated listing with filters (audience, date range, read status, keyword, category)
 * - Marking activities as read
 *
 * This client uses the unified /activities endpoint which combines:
 * - AI Agent activities (eve_mailer, eve_care, etc.)
 * - Document activities (upload, extract, classify)
 * - Tool executions
 * - External system events (JSONCargo status updates)
 * - System events (email sync, scheduled jobs)
 */

import { apiClient } from "@/lib/api-client";

// ============================================================================
// Types matching backend /activities API response (ActivityResponse)
// ============================================================================

export interface BackendActivity {
    activity_id: string;
    source_type: string;           // agent, tool, user, external, system
    activity_type: string;         // email_processed, document_uploaded, etc.
    activity_category: string;     // email, document, tracking, notification, etc.
    actor_type: string;            // agent, user, system, external_api
    actor_name: string;            // eve_mailer, user@email.com, system
    entity_type: string;           // email, document, container
    entity_id: string;
    expedition_id: string | null;
    container_number: string | null;
    title: string;
    summary: string;
    details: Record<string, unknown> | null;
    audience: string;
    show_in_feed: boolean;
    attention_level: 'action_needed' | 'require_review' | null;
    attention_reason: string | null;
    is_read: boolean;
    read_at: string | null;
    created_at: string;
    entity_deleted: boolean;       // true if related entity (container/document) has been deleted
}

export interface ActivitiesListResponse {
    activities: BackendActivity[];
    total: number;
    unread_count: number;
    attention_count: number;
    action_needed_count: number;
    require_review_count: number;
}

export interface ActivitiesFilters {
    /** Filter by source type: agent, tool, user, external, system */
    source_type?: string;
    /** Filter by activity category: email, document, tracking, notification */
    activity_category?: string;
    /** Filter by multiple activity categories (comma-separated) */
    activity_categories?: string[];
    /** Filter by audience: shipper, receiver, freightForwarder, carrier, customsBroker, all */
    audience?: string;
    /** Filter by read status (true = read, false = unread) */
    is_read?: boolean;
    /** Filter by attention level: action_needed, require_review */
    attention_level?: string;
    /** Only show feed items (default: true) */
    show_in_feed?: boolean;
    /** Filter from date (ISO 8601) */
    from_date?: string;
    /** Filter to date (ISO 8601) */
    to_date?: string;
    /** Search in title/summary */
    keyword?: string;
    /** Filter by entity type: email, document, container */
    entity_type?: string;
    /** Filter by expedition/container ID */
    expedition_id?: string;
    /** Number of records to skip (pagination) */
    skip?: number;
    /** Maximum records to return (pagination) */
    limit?: number;
}

export interface MarkReadResponse {
    marked_count: number;
    activity_ids: string[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch activities with optional filters
 *
 * @param filters - Optional filters for the activities query
 * @returns Paginated list of activities with total count
 */
export async function fetchActivities(
    filters?: ActivitiesFilters
): Promise<ActivitiesListResponse> {
    const params: Record<string, string | number | boolean> = {};

    if (filters?.source_type) {
        params.source_type = filters.source_type;
    }
    if (filters?.activity_category) {
        params.activity_category = filters.activity_category;
    }
    // Note: Multiple categories (activity_categories) are filtered client-side
    // The backend only accepts a single enum value for activity_category
    // See activity-feed.tsx for client-side filtering implementation
    if (filters?.audience) {
        params.audience = filters.audience;
    }
    if (filters?.is_read !== undefined) {
        params.is_read = filters.is_read;
    }
    if (filters?.attention_level !== undefined) {
        params.attention_level = filters.attention_level;
    }
    if (filters?.show_in_feed !== undefined) {
        params.show_in_feed = filters.show_in_feed;
    }
    if (filters?.from_date) {
        params.from_date = filters.from_date;
    }
    if (filters?.to_date) {
        params.to_date = filters.to_date;
    }
    if (filters?.keyword) {
        params.keyword = filters.keyword;
    }
    if (filters?.entity_type) {
        params.entity_type = filters.entity_type;
    }
    if (filters?.expedition_id) {
        params.expedition_id = filters.expedition_id;
    }
    if (filters?.skip !== undefined) {
        params.skip = filters.skip;
    }
    if (filters?.limit !== undefined) {
        params.limit = filters.limit;
    }

    return apiClient<ActivitiesListResponse>("/activities", {
        method: "GET",
        params,
    });
}

/**
 * Mark an activity as read
 *
 * @param activityId - The UUID of the activity to mark as read
 * @returns Confirmation with marked count
 */
export async function markActivityAsRead(
    activityId: string
): Promise<MarkReadResponse> {
    return apiClient<MarkReadResponse>(
        `/activities/${activityId}/read`,
        {
            method: "POST",
        }
    );
}

/**
 * Mark an activity as unread (reverse of mark as read)
 *
 * @param activityId - The UUID of the activity to mark as unread
 * @returns Confirmation
 */
export async function markActivityAsUnread(
    activityId: string
): Promise<MarkReadResponse> {
    return apiClient<MarkReadResponse>(
        `/activities/${activityId}/unread`,
        {
            method: "POST",
        }
    );
}

/**
 * Mark multiple activities as read
 *
 * @param activityIds - Array of activity UUIDs to mark as read
 * @returns Confirmation with marked count
 */
export async function markActivitiesAsRead(
    activityIds: string[]
): Promise<MarkReadResponse> {
    return apiClient<MarkReadResponse>(
        "/activities/mark-read",
        {
            method: "POST",
            body: JSON.stringify({ activity_ids: activityIds }),
        }
    );
}

export interface MarkAllReadResponse {
    marked_count: number;
}

/**
 * Mark all unread activities as read (system + email) for the given audience.
 * Use for "mark all as read" so both system logs and trade comms are updated.
 *
 * @param audience - Optional audience (e.g. "shipper"). If omitted, marks all.
 * @returns Number of activities marked
 */
export async function markAllActivitiesAsRead(
    audience?: string
): Promise<MarkAllReadResponse> {
    const params = audience ? { audience } : {};
    return apiClient<MarkAllReadResponse>("/activities/mark-all-read", {
        method: "POST",
        params,
    });
}

/**
 * Fetch unread activities count for a specific audience
 * Useful for showing badges in navigation
 *
 * @param audience - The audience to filter by
 * @returns Total count of unread activities
 */
export async function fetchUnreadCount(audience: string): Promise<number> {
    const response = await fetchActivities({
        audience,
        is_read: false,
        limit: 1, // We only need the total count
    });
    return response.unread_count;
}

/**
 * Fetch activities that require attention
 *
 * @param limit - Maximum number of activities to return
 * @returns Activities requiring attention
 */
export async function fetchAttentionRequired(
    limit: number = 50
): Promise<ActivitiesListResponse> {
    return apiClient<ActivitiesListResponse>("/activities/attention-required", {
        method: "GET",
        params: { limit },
    });
}

/**
 * Fetch activities for a specific expedition/container
 *
 * @param expeditionId - The expedition/container UUID
 * @param limit - Maximum number of activities to return
 * @returns Activities for the expedition
 */
export async function fetchActivitiesByExpedition(
    expeditionId: string,
    limit: number = 50,
    skip: number = 0
): Promise<ActivitiesListResponse> {
    return apiClient<ActivitiesListResponse>(`/activities/by-expedition/${expeditionId}`, {
        method: "GET",
        params: { limit, skip },
    });
}
